import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LocationPicker, Location } from '../components/LocationPicker';
import { PreferencesPanel } from '../components/PreferencesPanel';
import { SubscribeButtons } from '../components/SubscribeButtons';
import { SplashScreen } from '../components/SplashScreen';
import { FUNCTIONS_URL, supabase } from '../lib/supabase';
import { authCallbackUrl } from '../lib/urls';
import { refreshRow, useStore } from '../store';
import type { Settings } from '../lib/settings';

export function Manage() {
  const [params] = useSearchParams();
  const { session, row, loading } = useStore();
  const token = params.get('token');
  const [tokenSent, setTokenSent] = useState(false);
  const [email, setEmail] = useState('');
  const [signInStatus, setSignInStatus] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signInBusy, setSignInBusy] = useState(false);

  const sendSignInLink = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setSignInError('Enter a valid email.');
    }
    setSignInError(null);
    setSignInBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authCallbackUrl(), shouldCreateUser: false },
      });
      if (error) throw error;
      setSignInStatus('Check your email for a sign-in link.');
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setSignInBusy(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!session && token && !tokenSent) {
      void sendManageLink(token).then(() => setTokenSent(true));
    }
  }, [loading, session, token, tokenSent]);

  if (loading) return <SplashScreen title="Loading…" />;

  if (!session) {
    if (token) {
      return (
        <>
          <h1>Check your email</h1>
          <p className="muted">We sent a sign-in link to the email on file for this calendar.</p>
        </>
      );
    }
    return (
      <div className="centered-gate">
        <h1>Sign in</h1>
        <p className="muted">Enter the email you used when you purchased and we&#39;ll send you a sign-in link.</p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void sendSignInLink()}
          placeholder="you@example.com"
          aria-label="Email"
          autoComplete="email"
          style={{ marginTop: '0.75rem' }}
        />
        <button className="primary" onClick={sendSignInLink} disabled={signInBusy}>
          {signInBusy ? 'Sending…' : 'Send sign-in link'}
        </button>
        {signInStatus && <p className="notice">{signInStatus}</p>}
        {signInError && <p className="error">{signInError}</p>}
        <Link to="/" style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)', textDecoration: 'none' }}>&#8592; Back</Link>
      </div>
    );
  }

  if (!row) return <SetupPending email={session.user.email} />;

  return <ManageAuthenticated row={row} />;
}

function SetupPending({ email }: { email?: string }) {
  useEffect(() => {
    const id = setInterval(() => void refreshRow(), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="welcome-back welcome-back--steady">
      <p className="welcome-back-sun">🌅</p>
      <h1 className="welcome-back-heading">Almost there</h1>
      {email && <p className="welcome-back-email">{email}</p>}
      <p className="welcome-back-hint">Your calendar is still being set up…</p>
      <div className="welcome-back-footer">
        <button className="secondary" onClick={() => useStore.getState().signOut()}>
          Cancel and log out
        </button>
      </div>
    </div>
  );
}

function ManageAuthenticated({ row }: { row: NonNullable<ReturnType<typeof useStore.getState>['row']> }) {
  // Append updated_at as a cache-buster so that re-subscribing after a
  // settings/location change gets a URL calendar providers haven't cached
  // yet (Google in particular caches feeds by URL for up to ~24h).
  const icsUrl =
    `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/calendars/${row.cal_token}.ics` +
    `?v=${encodeURIComponent(row.updated_at)}`;
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [greeted, setGreeted] = useState(false);

  if (!greeted) {
    return (
      <SplashScreen
        title="Welcome back"
        subtitle={row.email}
        hint="Your calendar is ready."
        onDone={() => setGreeted(true)}
      />
    );
  }

  const updateLocation = async (loc: Location) => {
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch(`${FUNCTIONS_URL}/user-location`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ lat: loc.lat, lng: loc.lng }),
      });
      if (!res.ok) throw new Error(`server returned ${res.status}`);
      await refreshRow();
      setMsg('Location updated — calendar regenerated.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'update failed');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = async (patch: Partial<Settings>) => {
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch(`${FUNCTIONS_URL}/user-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ settings: { ...row.settings, ...patch } }),
      });
      if (!res.ok) throw new Error(`server returned ${res.status}`);
      await refreshRow();
      setMsg('Settings saved.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-fade-in">
      <h1>Welcome back, <span style={{ color: 'var(--primary)' }}>{row.email}</span></h1>

      <LocationPicker
        value={{ lat: row.lat, lng: row.lng, label: 'Your saved location', source: 'manual' }}
        onChange={updateLocation}
        autoRequestGeolocation={false}
      />

      <h2>Your calendar link</h2>
      <SubscribeButtons icsUrl={icsUrl} caption="Live 30-day feed. It updates every day." />

      <h2>Preferences</h2>
      <p className="soft" style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
        Changed something? The buttons above now point to a fresh link, so subscribing
        again adds a new &quot;Outside Calendar&quot; alongside your old one — it won&#39;t
        replace it. Subscribe to the new link, then remove the old &quot;Outside
        Calendar&quot; from your calendar app so you don&#39;t see both.
      </p>
      <PreferencesPanel value={row.settings} onChange={updateSettings} />

      {saving && <p className="muted">Saving…</p>}
      {msg && <p className="notice">{msg}</p>}
      {err && <p className="error">{err}</p>}

      <div className="details-body">
        <h3>What is this?</h3>
        <p>
          A calendar feed that adds sunrise and sunset times to your existing calendar app —
          Google Calendar, Apple Calendar, Outlook, or anything that subscribes to a URL. Your
          location decides the times.
        </p>
        <h3>How it works</h3>
        <p>
          Pick your location on the map. Click Google, Apple, or Outlook. Your calendar app
          subscribes to a feed at a stable URL. The feed rewrites daily for your location, so
          the next day&#39;s sunrise is always there without you doing anything.
        </p>
        <h3>Accuracy and timezones</h3>
        <p>
          Times come from standard astronomical formulas using your exact coordinates. Events
          appear in your local timezone, derived from your location — no configuration.
        </p>
        <h3>Is this a subscription?</h3>
        <p>
          No. $2 covers 3 years of updates, and nothing auto-renews. When the 3 years are
          up, you can pay $2 again to continue — nothing recurring or automatic.
        </p>
      </div>

      <button className="secondary" style={{ marginTop: '2rem' }} onClick={() => useStore.getState().signOut()}>
        Sign out
      </button>
    </div>
  );
}

async function sendManageLink(calToken: string) {
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  await fetch(`${FUNCTIONS_URL}/manage-link-send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${anon}` },
    body: JSON.stringify({ cal_token: calToken }),
  });
}
